import { registerSchema, hasSchema } from '@hyperjump/json-schema/draft-2020-12';
import { compile, interpret, BASIC, getSchema } from '@hyperjump/json-schema/experimental';
import * as Instance from '@hyperjump/json-schema/instance/experimental';

import Strings from '../../Strings';

/* eslint-disable no-bitwise */
// A simple, *insecure* 32-bit hash that's short, fast, and has no dependencies.
// Output is always 7 characters.
// Loosely based on the Java version; see
// https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
export const simpleHash = str => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
  }
  // Convert to 32bit unsigned integer in base 36 and pad with "0" to ensure length is 7.
  return (hash >>> 0).toString(36).padStart(7, '0');
};

const decodePointerSegment = segment =>
  segment.replace(/~1/g, '/').replace(/~0/g, '~');

const getPointerSegments = pointer => {
  if (!pointer) {
    return [];
  }
  const trimmed = pointer.startsWith('#') ? pointer.slice(1) : pointer;
  if (!trimmed) {
    return [];
  }
  const withoutSlash = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  if (!withoutSlash) {
    return [];
  }
  return withoutSlash.split('/').map(decodePointerSegment);
};

const getValueAtPointer = (root, pointer) => {
  const segments = getPointerSegments(pointer);
  return segments.reduce((current, segment) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    return current[segment];
  }, root);
};

const normalizeInstanceLocation = instanceLocation => {
  if (!instanceLocation) {
    return '#';
  }
  const hashIndex = instanceLocation.indexOf('#');
  if (hashIndex === -1) {
    if (instanceLocation.startsWith('#')) {
      return instanceLocation;
    }
    if (instanceLocation.startsWith('/')) {
      return `#${instanceLocation}`;
    }
    return '#';
  }
  const fragment = instanceLocation.slice(hashIndex + 1);
  return fragment ? `#${fragment}` : '#';
};

const normalizeInstancePath = (instanceLocation, missingProperty) => {
  const basePath = instanceLocation || '';
  if (!missingProperty) {
    return basePath;
  }
  const separator = basePath.endsWith('/') || basePath === '' ? '' : '/';
  return `${basePath}${separator}${missingProperty}`;
};

const toPropertyPath = instanceLocation => {
  if (!instanceLocation) {
    return '';
  }
  const normalizedLocation = instanceLocation.startsWith('#')
    ? instanceLocation.slice(1)
    : instanceLocation;
  const parts = normalizedLocation
    .split('/')
    .filter(Boolean)
    .map(decodePointerSegment);
  return parts.length ? `.${parts.join('.')}` : '';
};

const buildErrorSchema = errors => {
  const errorSchema = { __errors: [] };

  errors.forEach(error => {
    const propertyPath = toPropertyPath(error.instanceLocation);
    const pathParts = propertyPath.split('.').filter(Boolean);
    let current = errorSchema;
    if (pathParts.length === 0) {
      current.__errors.push(error.message);
      return;
    }
    pathParts.forEach(part => {
      if (!current[part]) {
        current[part] = { __errors: [] };
      }
      current = current[part];
    });
    current.__errors.push(error.message);
  });

  return errorSchema;
};

const toRjsfError = error => {
  const property = toPropertyPath(error.instanceLocation);
  const name = error.keyword === 'const' ? 'validation' : (error.keyword || 'validation');
  return {
    name,
    property,
    message: error.message,
    params: error.params || {},
    stack: error.message,
  };
};

const schemaRecordCache = new WeakMap();
const schemaRootCache = new Map();
const compiledSchemaCache = new Map();
const compilePromises = new Map();

const getSchemaRecord = schema => {
  if (schemaRecordCache.has(schema)) {
    return schemaRecordCache.get(schema);
  }
  const baseId = typeof schema.$id === 'string' ? schema.$id : 'urn:dsapi:schema';
  const schemaHash = simpleHash(JSON.stringify(schema));
  const schemaId = `${baseId}_${schemaHash}`;
  const normalizedSchema = schema.$id === schemaId ? schema : { ...schema, $id: schemaId };
  const record = { id: schemaId, schema: normalizedSchema };
  schemaRecordCache.set(schema, record);
  if (!schemaRootCache.has(schemaId)) {
    schemaRootCache.set(schemaId, normalizedSchema);
  }
  return record;
};

const ensureSchemaCompiled = schema => {
  const record = getSchemaRecord(schema);
  if (compiledSchemaCache.has(record.id) || compilePromises.has(record.id)) {
    return;
  }
  const compilePromise = (async () => {
    if (!hasSchema(record.id)) {
      registerSchema(record.schema, record.id);
    }
    const browserSchema = await getSchema(record.id);
    const compiled = await compile(browserSchema);
    compiledSchemaCache.set(record.id, compiled);
  })()
    .catch((error) => {
      // Validation is best-effort until the schema is compiled.
      console.error('Failed to compile JSON schema for validation.', error);
    })
    .finally(() => {
      compilePromises.delete(record.id);
    });

  compilePromises.set(record.id, compilePromise);
};

const getSchemaValidator = schema => {
  if (!schema || typeof schema !== 'object') {
    return null;
  }
  const record = getSchemaRecord(schema);
  const compiled = compiledSchemaCache.get(record.id);
  if (!compiled) {
    ensureSchemaCompiled(schema);
    return null;
  }
  return (formData, outputFormat = BASIC) =>
    interpret(compiled, Instance.fromJs(formData), outputFormat);
};

const flattenHyperjumpErrors = (errors, acc = []) => {
  if (!Array.isArray(errors)) {
    return acc;
  }
  errors.forEach(error => {
    acc.push(error);
    if (Array.isArray(error.errors)) {
      flattenHyperjumpErrors(error.errors, acc);
    }
  });
  return acc;
};

const getKeywordName = keyword => {
  if (!keyword) {
    return 'validation';
  }
  const lastSlash = keyword.lastIndexOf('/');
  return lastSlash >= 0 ? keyword.slice(lastSlash + 1) : keyword;
};

const getSchemaValueAtLocation = (absoluteKeywordLocation, schemaId) => {
  if (!absoluteKeywordLocation) {
    return undefined;
  }
  const hashIndex = absoluteKeywordLocation.indexOf('#');
  const baseUri = hashIndex === -1
    ? absoluteKeywordLocation
    : absoluteKeywordLocation.slice(0, hashIndex);
  const schemaRoot = schemaRootCache.get(baseUri) || schemaRootCache.get(schemaId);
  if (!schemaRoot) {
    return undefined;
  }
  if (hashIndex === -1) {
    return schemaRoot;
  }
  return getValueAtPointer(schemaRoot, absoluteKeywordLocation.slice(hashIndex + 1));
};

const getInstanceValue = (formData, instancePointer) => {
  if (!instancePointer) {
    return formData;
  }
  return getValueAtPointer(formData, instancePointer.startsWith('#') ? instancePointer.slice(1) : instancePointer);
};

const buildMessage = (keywordName, schemaValue) => {
  switch (keywordName) {
    case 'required':
      return 'Field is required';
    case 'type': {
      if (Array.isArray(schemaValue)) {
        return `Must be one of: ${schemaValue.join(', ')}`;
      }
      return schemaValue ? `Must be ${schemaValue}` : 'Invalid type';
    }
    case 'enum':
      return 'Value must be one of the allowed options';
    case 'const':
      return schemaValue !== undefined
        ? `Value must be equal to ${schemaValue}`
        : 'Value must match the required constant';
    case 'minimum':
      return `Value must be >= ${schemaValue}`;
    case 'maximum':
      return `Value must be <= ${schemaValue}`;
    case 'minLength':
      return `Must be at least ${schemaValue} characters`;
    case 'maxLength':
      return `Must be at most ${schemaValue} characters`;
    case 'pattern':
      return 'Value does not match the required pattern';
    default:
      return 'Invalid value';
  }
};

const normalizeHyperjumpErrors = (rawErrors, schema, formData) => {
  const record = getSchemaRecord(schema);
  const flattened = flattenHyperjumpErrors(rawErrors);
  const normalizedErrors = [];

  flattened.forEach(error => {
    const keywordName = getKeywordName(error.keyword);
    const instancePointer = normalizeInstanceLocation(error.instanceLocation);
    const schemaValue = getSchemaValueAtLocation(error.absoluteKeywordLocation, record.id);

    if (keywordName === 'required') {
      const instanceValue = getInstanceValue(formData, instancePointer);
      if (Array.isArray(schemaValue) && instanceValue && typeof instanceValue === 'object') {
        const missingProps = schemaValue.filter(prop =>
          !Object.prototype.hasOwnProperty.call(instanceValue, prop)
        );
        if (missingProps.length > 0) {
          missingProps.forEach(prop => {
            normalizedErrors.push({
              keyword: 'required',
              instanceLocation: normalizeInstancePath(instancePointer, prop),
              params: { missingProperty: prop },
              message: 'Field is required',
            });
          });
          return;
        }
      }
    }

    const params = keywordName === 'const' ? { allowedValue: schemaValue } : {};
    normalizedErrors.push({
      keyword: keywordName,
      instanceLocation: instancePointer,
      params,
      message: buildMessage(keywordName, schemaValue),
    });
  });

  return normalizedErrors;
};

export const transformErrors = (errors, uiSchema) => {
  return errors.map((error) => {
    const newError = { ...error };
    if (newError.name === 'format') {
      newError.message = Strings.capPatient.incorrectFieldFormat;
    }
    if (newError.name === 'validation' || newError.name === 'const') {
      if (newError?.property?.endsWith('module_enabled') && newError?.params?.allowedValue === true) {
        newError.message = 'To disable this module, please contact support.';
      } else {
        newError.message = `Value must be equal to ${newError?.params?.allowedValue}`;
      }
    }
    return newError;
  });
}

export const validator = {
  prepareSchema(schema) {
    if (schema && typeof schema === 'object') {
      ensureSchemaCompiled(schema);
    }
  },
  validateFormData(formData, schema, customValidate, transformErrorsFn, uiSchema) {
    const validate = getSchemaValidator(schema);
    if (!validate) {
      return { errors: [], errorSchema: {} };
    }
    let output;
    try {
      output = validate(formData, BASIC);
    } catch (error) {
      const message = error?.message || 'Validation failed';
      return {
        errors: [{
          name: 'validation',
          property: '',
          message,
          params: {},
          stack: message,
        }],
        errorSchema: { __errors: [message] },
      };
    }
    const rawErrors = output?.valid ? [] : (output?.errors || []);
    const normalizedErrors = normalizeHyperjumpErrors(rawErrors, schema, formData);
    let errors = normalizedErrors.map(toRjsfError);
    if (transformErrorsFn) {
      errors = transformErrorsFn(errors, uiSchema);
    }
    
    // Convert transformed errors back to normalized format for errorSchema
    const transformedNormalizedErrors = errors.map(error => ({
      keyword: error.name === 'validation' ? 'validation' : error.name,
      instanceLocation: error.property,
      params: error.params || {},
      message: error.message,
    }));
    
    return {
      errors,
      errorSchema: buildErrorSchema(transformedNormalizedErrors),
    };
  },
  isValid(schema, formData) {
    const validate = getSchemaValidator(schema);
    if (!validate) {
      return true;
    }
    try {
      return validate(formData, BASIC).valid;
    } catch (error) {
      return false;
    }
  },
};
